import React, { useContext } from 'react';
import styled from '@emotion/styled';
import { MainWrapper } from '@zds3/zds-components';
import { NavigationContext } from '../NavigationContext';
import { useEnvironmentBar } from './hooks/useEnvironmentBar';
import { Alert } from '@platform-ui/design-system';
import LegalFooter from './legalFooter/LegalFooter';

if (window.customElements.get('z-main-wrapper') === undefined) {
  window.customElements.define('z-main-wrapper', MainWrapper);
}

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'z-main-wrapper': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
      slot: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLSlotElement>,
        HTMLSlotElement
      > & { name?: string };
    }
  }
}

const MainWrapperContainer = styled.div<{
  $padding: boolean;
  $isZASTokenEnabled: boolean;
  $dynamicTop: string;
}>`
  flex: 1;
  min-width: 0;
  min-height: 0;
  width: 100%;
  height: 100%;
  overflow-x: auto;
  box-sizing: border-box;

  /* Deliberately no transition. This element wraps the whole host page, and the
   * only animatable property that changes on it is padding-top — the offset that
   * compensates for the fixed-position environment bar. Animating that left the
   * bar overlapping the page content for half a second, out of sync with the
   * instant top shift z-navigation applies inline, and relaid out the entire
   * page on every frame of the animation. */
  padding-top: ${({ $dynamicTop }) => $dynamicTop};

  ${({ $padding }) =>
    $padding &&
    `
      & ::slotted([slot='pageContent']) {
        padding: 1.5rem;
      }
  `}
`;

export const MainWrapperComponent = ({
  isPartialOrPartialPlus,
  isMainWrapperWithoutPadding = false,
  isZASTokenEnabled,
}: {
  isPartialOrPartialPlus: boolean | undefined;
  isMainWrapperWithoutPadding: boolean;
  isZASTokenEnabled: boolean;
}) => {
  const { pageAlert } = useContext(NavigationContext);
  const { totalBarHeight } = useEnvironmentBar();
  const dynamicTop = totalBarHeight ? `${totalBarHeight}px` : '';

  /* Render the slots on the first pass. There is nothing to wait for:
   * z-main-wrapper is defined at the top of this module, it attaches its shadow
   * root in its constructor, and it builds the inner slot synchronously in
   * connectedCallback — so projection works the moment React inserts it.
   *
   * This used to sit behind a 100ms setTimeout. Until it elapsed no
   * `slot[name="pageContent"]` existed, so the host's page content was not
   * projected and therefore not laid out or painted at all: a fixed delay to the
   * host's first paint, plus a full-page relayout when the slot appeared.
   */
  return (
    <MainWrapperContainer
      $isZASTokenEnabled={isZASTokenEnabled}
      $padding={!isPartialOrPartialPlus && !isMainWrapperWithoutPadding}
      $dynamicTop={dynamicTop}
    >
      <z-main-wrapper disable-zoom-out>
        <slot name="breadcrumbContent" />
        {pageAlert && <Alert {...pageAlert} />}
        <slot name="pageContent" />
        {!isPartialOrPartialPlus && <LegalFooter />}
      </z-main-wrapper>
    </MainWrapperContainer>
  );
};
